<template>
  <div class="station-subheader">
    <div class="subheader-left">
      <button class="btn-back" @click="$emit('back')" title="Back to stations"><AppIcon name="arrow-left" /></button>
      <div class="breadcrumb-path">
        <span class="breadcrumb-parent">Stations</span>
        <span class="breadcrumb-divider">/</span>
        <span class="breadcrumb-current">{{ station ? station.name : 'Station' }}</span>
      </div>
    </div>
    <div class="subheader-right">
      <div class="datetime-picker">
        <input type="datetime-local" id="datetime-input" ref="datetimeInput" step="1" :max="maxDatetime">
        <button id="btn-goto-datetime" @click="gotoDatetime">Go</button>
      </div>
      <div class="time-range-buttons desktop-only">
        <button v-for="(range, idx) in timeRanges" :key="range.label" :data-range="idx" :class="{ active: idx === activeRangeIndex }" @click="setRange(idx)">{{ range.label }}</button>
        <button id="btn-live-range" @click="goLive">Live</button>
      </div>
      <div class="time-range-dropdown mobile-only">
        <select :value="activeRangeIndex" @change="setRange(parseInt($event.target.value))">
          <option v-for="(range, idx) in timeRanges" :key="range.label" :value="idx">{{ range.label }}</option>
        </select>
        <button class="btn-live-mobile" @click="goLive">Live</button>
      </div>
      <div class="selection-controls">
        <button id="btn-toggle-selection" :class="{ active: selectionMode }" @click="toggleSelection">Select Region</button>
      </div>
      <div class="header-menu-wrapper" ref="menuWrapper">
        <button class="btn-header-menu" @click="toggleMenu" title="More options">
          <AppIcon name="ellipsis-h" />
        </button>
        <div v-if="menuOpen" class="header-menu">
          <router-link v-if="station" :to="`/station/${station.slug}/exports`" class="header-menu-item" @click="menuOpen = false">
            <AppIcon name="file-export" />
            <span>Exports</span>
          </router-link>
          <router-link v-if="station && features.voices" :to="`/station/${station.slug}/voices`" class="header-menu-item" @click="menuOpen = false">
            <AppIcon name="microphone" />
            <span>Voices</span>
          </router-link>
          <router-link v-if="station" :to="`/station/${station.slug}/stats`" class="header-menu-item" @click="menuOpen = false">
            <AppIcon name="chart-bar" />
            <span>Statistics</span>
          </router-link>
        </div>
      </div>
      <div class="status">
        <span id="source-status" :class="['status-dot', { online: sourceConnected }]"></span>
        <span id="source-text">{{ sourceConnected ? 'Online' : 'Offline' }}</span>
        <span id="live-indicator" :class="['live-badge', { hidden: !showLiveBadge }]">LIVE</span>
      </div>
    </div>

    <ConfirmModal
      :visible="showSelectionWarning"
      type="warning"
      title="Cannot Select Region"
      message="Please pause playback and navigate to a recorded section before selecting a region."
      confirmText="OK"
      :showCancel="false"
      @confirm="showSelectionWarning = false"
      @cancel="showSelectionWarning = false"
    />
  </div>
</template>

<script>
import { authFetch } from '../services/api'
import { mapState, mapGetters } from 'vuex'
import ConfirmModal from './ConfirmModal.vue'

export default {
  name: 'AppHeader',
  components: { ConfirmModal },
  props: {
    station: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showSelectionWarning: false,
      menuOpen: false,
      features: { voices: true, socialVideo: true }
    }
  },
  computed: {
    ...mapState(['timeRanges', 'sourceConnected', 'isLive', 'selectionMode', 'viewDuration', 'isPlaying']),
    ...mapGetters(['activeRangeIndex', 'canEnterSelectionMode']),
    showLiveBadge() { return this.isLive && this.sourceConnected },
    maxDatetime() {
      return this.formatForInput(new Date())
    }
  },
  async mounted() {
    try {
      const res = await authFetch('/api/features')
      if (res.ok) this.features = await res.json()
    } catch (e) { /* ignore */ }
    this.updateDatetimeInput()
    this._closeMenu = (e) => {
      if (this.menuOpen && !this.$refs.menuWrapper?.contains(e.target)) {
        this.menuOpen = false
      }
    }
    document.addEventListener('click', this._closeMenu)
  },
  beforeUnmount() {
    document.removeEventListener('click', this._closeMenu)
  },
  methods: {
    formatForInput(d) {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    updateDatetimeInput() {
      if (this.$refs.datetimeInput) {
        this.$refs.datetimeInput.value = this.formatForInput(new Date())
      }
    },
    setRange(idx) {
      this.$store.dispatch('setViewDuration', this.timeRanges[idx].ms)
    },
    goLive() {
      this.$store.dispatch('goLive').then(() => {
        this.$emit('go-live')
      })
    },
    gotoDatetime() {
      const input = this.$refs.datetimeInput
      if (!input || !input.value) return
      const timestamp = new Date(input.value).getTime()
      if (isNaN(timestamp)) return
      this.$emit('goto-timestamp', timestamp)
    },
    toggleSelection() {
      if (!this.selectionMode && !this.canEnterSelectionMode) {
        this.showSelectionWarning = true
        return
      }
      this.$store.dispatch('toggleSelectionMode')
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen
    },
  }
}
</script>

<style scoped>
.station-subheader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 32px;
  background: var(--white);
  border-bottom: 1px solid var(--gray-200);
  flex-shrink: 0;
}

.subheader-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-back:hover {
  background: var(--gray-50);
  border-color: var(--gray-400);
  color: var(--gray-800);
}

.breadcrumb-path {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.breadcrumb-parent {
  color: var(--gray-500);
  font-weight: 500;
}

.breadcrumb-divider {
  color: var(--gray-400);
}

.breadcrumb-current {
  color: var(--gray-900);
  font-weight: 600;
}

.subheader-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.datetime-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.datetime-picker input[type="datetime-local"] {
  padding: 6px 10px;
  border: 1px solid var(--gray-200);
  background: var(--white);
  color: var(--gray-700);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  border-radius: 4px;
  outline: none;
  transition: all 0.2s;
}

.datetime-picker input[type="datetime-local"]:focus {
  border-color: var(--gray-400);
}

.datetime-picker button {
  padding: 6px 12px;
  border: 1px solid var(--gray-200);
  background: var(--white);
  color: var(--gray-600);
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.datetime-picker button:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.time-range-buttons {
  display: flex;
  gap: 4px;
}

.time-range-buttons button {
  padding: 6px 12px;
  border: 0.5px solid var(--gray-200);
  background: var(--white);
  color: var(--gray-600);
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.time-range-buttons button:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.time-range-buttons button.active {
  background: #262626;
  color: var(--white);
  border-color: #262626;
}

#btn-live-range {
  color: #262626;
  border-color: #262626;
}

#btn-live-range:hover {
  background: #262626;
  color: var(--white);
}

.selection-controls {
  display: flex;
  align-items: center;
}

#btn-toggle-selection {
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #3393ff;
  border-radius: 4px;
  background: #3393ff;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

#btn-toggle-selection:hover {
  background: #1a7ee6;
  border-color: #1a7ee6;
}

#btn-toggle-selection.active {
  background: #262626;
  color: var(--white);
  border-color: #262626;
}

.status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--gray-500);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-300);
}

.status-dot.online {
  background: var(--success);
}

.live-badge {
  padding: 3px 8px;
  background: #262626;
  color: var(--white);
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.live-badge.hidden {
  display: none;
}

.header-menu-wrapper {
  position: relative;
}

.btn-header-menu {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: none;
  border: 0.7px solid var(--gray-300);
  border-radius: 6px;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-header-menu:hover {
  background: var(--gray-50);
  border-color: var(--gray-400);
  color: var(--gray-800);
}

.header-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  min-width: 180px;
  z-index: 9999;
  overflow: hidden;
}

.header-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  font-size: 13px;
  color: var(--gray-700);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.header-menu-item:hover {
  background: var(--gray-50);
}

/* Responsive time range controls */
.desktop-only {
  display: flex;
}

.mobile-only {
  display: none !important;
}

.time-range-dropdown {
  display: flex;
  align-items: center;
  gap: 4px;
}

.time-range-dropdown select {
  padding: 6px 10px;
  border: 1px solid var(--gray-200);
  background: var(--white);
  color: var(--gray-700);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;
}

.time-range-dropdown select:focus {
  border-color: var(--gray-400);
}

.btn-live-mobile {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #262626;
  border-radius: 4px;
  background: var(--white);
  color: #262626;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-live-mobile:hover {
  background: #262626;
  color: var(--white);
}

@media (max-width: 1215px) {
  .desktop-only {
    display: none;
  }
  
  .mobile-only {
    display: flex !important;
  }
  
  .subheader-right {
    gap: 12px;
  }
  
  .datetime-picker {
    display: none;
  }
}

@media (max-width: 900px) {
  .station-subheader {
    padding: 12px 16px;
  }
  
  .breadcrumb-parent,
  .breadcrumb-divider {
    display: none;
  }
}

@media (max-width: 768px) {
  .subheader-right {
    gap: 8px;
  }
  
  .selection-controls {
    display: none;
  }
  
  .status #source-text {
    display: none;
  }
}
</style>
